// Lists every song that has been mirrored into IndexedDB and lets
// the user free space by removing individual entries.
//
// Also surfaces the storage quota and the persistence state.
// Hidden behind OFFLINE_ENABLED — when the flag is off the route
// renders a "Offline-Modus deaktiviert" hint.

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import { OFFLINE_ENABLED } from '../config/offline'
import {
  listOfflineSongs,
  type CachedSongRecord,
} from '../lib/offline/songCache'
import { syncPending, getPendingCount } from '../lib/offline/syncQueue'
import { useOnlineStatus } from '../lib/offline/useOnlineStatus'
import {
  estimateSongOfflineBytes,
  removeSongFromOffline,
} from '../lib/offline/autoCache'
import { formatBytes, isPersistent, requestPersistence } from '../lib/offline/quota'
import { QuotaBar } from '../components/offline/QuotaBar'
import { OnlineIndicator } from '../components/offline/OnlineIndicator'

export function OfflineSongsPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [records, setRecords] = useState<CachedSongRecord[] | null>(null)
  const [persisted, setPersisted] = useState<boolean | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [syncing, setSyncing] = useState(false)
  const status = useOnlineStatus()

  const refresh = useCallback(async () => {
    const list = await listOfflineSongs()
    list.sort((a, b) => b.cachedAt - a.cachedAt)
    setRecords(list)
    setPersisted(await isPersistent())
    setPendingCount(await getPendingCount())
  }, [])

  async function handleSyncNow() {
    setSyncing(true)
    try {
      await syncPending()
      await refresh()
    } finally {
      setSyncing(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleRemove(record: CachedSongRecord) {
    if (!confirm(`"${record.song.title || 'Ohne Titel'}" aus dem Offline-Speicher entfernen?`)) {
      return
    }
    await removeSongFromOffline(record.song)
    await refresh()
  }

  async function handleRequestPersist() {
    const ok = await requestPersistence()
    setPersisted(ok)
  }

  if (!OFFLINE_ENABLED) {
    return (
      <div className="flex min-h-full flex-col">
        <header className="flex items-center justify-between px-4 py-3">
          <button
            className="flex items-center gap-2 text-text-muted hover:text-text"
            onClick={() => navigate('/songs')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
            <span className="text-lg font-bold text-text">{t('offline.title')}</span>
          </button>
          <OnlineIndicator />
        </header>
        <main className="flex-1 px-4 py-10 text-center text-text-muted">
          {t('offline.disabled')}
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-center justify-between px-4 py-3">
        <button
          className="flex items-center gap-2 text-text-muted hover:text-text"
          onClick={() => navigate('/songs')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
          <span className="text-lg font-bold text-text">{t('offline.title')}</span>
        </button>
        <OnlineIndicator />
      </header>

      <main className="flex-1 space-y-4 px-4 pb-8">
        <section className="rounded-lg border border-surface-lighter bg-surface-light p-4">
          <h2 className="mb-2 text-sm font-semibold text-text">
            {t('offline.storage')}
          </h2>
          <QuotaBar />
          <div className="mt-3 flex items-center justify-between gap-3 text-xs">
            {persisted ? (
              <span className="flex items-center gap-1 text-success">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {t('offline.persistGranted')}
              </span>
            ) : (
              <>
                <span className="text-text-muted">{t('offline.persistMissing')}</span>
                <button
                  className="rounded border border-surface-lighter px-2 py-1 text-text-muted active:bg-surface-lighter"
                  onClick={handleRequestPersist}
                >
                  {t('offline.persistRequest')}
                </button>
              </>
            )}
          </div>
        </section>

        {pendingCount > 0 && (
          <section className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
            <h2 className="mb-1 text-sm font-semibold text-text">
              {t('offline.pendingSync', { count: String(pendingCount) })}
            </h2>
            <p className="mb-3 text-xs text-text-muted">
              {t('offline.pendingSyncHint')}
            </p>
            <button
              className="rounded border border-surface-lighter bg-surface-light px-3 py-1.5 text-xs text-text disabled:opacity-50"
              onClick={handleSyncNow}
              disabled={syncing || status === 'offline'}
            >
              {syncing ? t('offline.syncing') : t('offline.syncNow')}
            </button>
          </section>
        )}

        <section>
          <h2 className="mb-2 text-sm font-semibold text-text">
            {t('offline.songs')}
            {records && (
              <span className="ml-2 text-xs font-normal text-text-muted">
                ({records.length})
              </span>
            )}
          </h2>

          {records === null ? (
            <p className="py-6 text-center text-xs text-text-muted">{t('general.loading')}</p>
          ) : records.length === 0 ? (
            <p className="py-6 text-center text-xs text-text-muted">{t('offline.empty')}</p>
          ) : (
            <div className="space-y-2">
              {records.map((record) => {
                const bytes = estimateSongOfflineBytes(record.song)
                return (
                  <div
                    key={record.id}
                    className="flex items-center gap-3 rounded-lg border border-surface-lighter bg-surface-light p-3"
                  >
                    <button
                      className="min-w-0 flex-1 text-left"
                      onClick={() => navigate(`/songs/${record.song.id}`)}
                    >
                      <p className="truncate text-sm font-medium text-text">
                        {record.song.title || t('songbook.untitled')}
                      </p>
                      <p className="text-xs text-text-muted">
                        {formatBytes(bytes)}
                        {record.source === 'shared' && (
                          <span className="ml-2 rounded bg-primary/20 px-1.5 py-0.5 text-[10px] uppercase text-primary">
                            shared
                          </span>
                        )}
                      </p>
                    </button>
                    <button
                      className="shrink-0 rounded p-1.5 text-text-muted hover:text-error"
                      onClick={() => handleRemove(record)}
                      title={t('offline.removeFromOffline')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  )
}
